import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { ABMEnrollmentStatusEnum } from '@electricjs/core_entity-client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import {
  useGetOrganizationEmployeesQuery,
  useGetOrganizationApplicationsQuery,
  useGetOrganizationGroupsQuery,
  useGetOrganizationAdditionalInfoQuery,
} from '@/redux/slices/organizationApiSlice';
import {
  useCheckBillingInfoExistsQuery,
  useGetOrganizationPaymentMethodsQuery,
} from '@/redux/slices/billingApiSlice';
import { useGetJumpcloudProvisionStatusQuery } from '@/redux/slices/mdmManagementApiSlice';
import {
  MdmCertExpirationStatus,
  useGetMdmCertExpirationStatus,
} from '@/hooks/useGetMdmCertExpiration';
import useIntegrationProvider from '@/components/Settings/IntegrationsPane/HRISIntegrations/useIntegrationProvider';
import { useOrganizationHasEntitlements } from '@/hooks/useOrganizationHasEntitlement';
import { useGettingStartedStorage } from '@/pages/Dashboard/Sections/GettingStarted/hooks/useGettingStartedStorage';
import { OrganizationUserRoleSlugs } from '@/types/users';
import { GETTING_STARTED_STEPS } from '@/types/aiChat';
import type {
  GettingStartedCompletionState,
  GettingStartedStepKey,
} from '@/types/aiChat';

type UseGettingStartedCompletionStateOptions = {
  enabled: boolean;
};

type UseGettingStartedCompletionStateResult = {
  gettingStartedState: GettingStartedCompletionState | null;
  incompleteStepKeys: GettingStartedStepKey[];
  hasIncompleteSteps: boolean;
  isLoading: boolean;
};

// Legacy priority order used when gettingStartedModuleImprovements is off.
// New steps (connect_app_integration, configure_ztp, notify_team) belong only in
// the improvements-on branch below — do NOT add them here.
const GS_STEP_PRIORITY_ORDER: GettingStartedStepKey[] = [
  GETTING_STARTED_STEPS.CONNECT_HR_SYSTEM,
  GETTING_STARTED_STEPS.ADD_EMPLOYEES,
  GETTING_STARTED_STEPS.INVITE_ADMINS,
  GETTING_STARTED_STEPS.ADD_APPLICATIONS,
  GETTING_STARTED_STEPS.ADD_GROUPS,
  GETTING_STARTED_STEPS.ENABLE_MDM,
  GETTING_STARTED_STEPS.ADD_PAYMENT_METHOD,
  GETTING_STARTED_STEPS.CONFIGURE_EMPLOYEE_SUPPORT,
  GETTING_STARTED_STEPS.CONNECT_ABM,
  GETTING_STARTED_STEPS.INVITE_EMPLOYEES,
];

export const useGettingStartedCompletionState = ({
  enabled,
}: UseGettingStartedCompletionStateOptions): UseGettingStartedCompletionStateResult => {
  const organizationId = useGetOrganizationId();
  const skip = !enabled || !organizationId;
  const queryArg = skip ? skipToken : { organizationId: organizationId! };
  const orgIdArg = skip ? skipToken : organizationId!;

  const { hrisIntegrationId, isLoadingProvider } = useIntegrationProvider();
  const { data: employees, isFetching: employeesFetching } =
    useGetOrganizationEmployeesQuery(queryArg);
  const { data: orgAppsResponse, isFetching: appsFetching } =
    useGetOrganizationApplicationsQuery(queryArg);
  const { data: orgGroupsResponse, isFetching: groupsFetching } =
    useGetOrganizationGroupsQuery(queryArg);
  const { data: hasBillingInfo, isFetching: billingInfoFetching } =
    useCheckBillingInfoExistsQuery(orgIdArg);
  const { data: paymentMethods, isFetching: paymentMethodsFetching } =
    useGetOrganizationPaymentMethodsQuery(orgIdArg);
  const { status: mdmCertExpirationStatus, isLoading: mdmCertLoading } =
    useGetMdmCertExpirationStatus();
  const { data: provisionStatusData, isFetching: mdmFetching } =
    useGetJumpcloudProvisionStatusQuery(skip ? skipToken : organizationId!);
  const { data: orgAdditionalInfoData, isLoading: additionalInfoLoading } =
    useGetOrganizationAdditionalInfoQuery(queryArg);

  const {
    hasApplicationsEntitlement,
    hasEmployeeGroupsEntitlement,
    hasJumpcloudMdmEntitlement: hasMdmEntitlement,
    hasEmployeeUserAccountsEntitlement,
    hasHrisIntegrationEntitlement,
    hasDirectHrisIntegrationEntitlement,
    hasXboardingsEntitlement,
    hasSupportCenterEntitlement,
  } = useOrganizationHasEntitlements();

  const {
    skipAbm,
    skipMacMdm,
    skipItChampions,
    skipHr,
    skipGroups,
    skipPaymentMethod,
    skipIntegrations,
    ztpConfigured,
    notifyTeam,
    inviteEmployees,
  } = useGettingStartedStorage();

  const { gettingStartedModuleImprovements, goLive } = useFlags();

  return useMemo((): UseGettingStartedCompletionStateResult => {
    if (skip) {
      return {
        gettingStartedState: null,
        incompleteStepKeys: [],
        hasIncompleteSteps: false,
        isLoading: false,
      };
    }

    const isLoading =
      isLoadingProvider ||
      employeesFetching ||
      appsFetching ||
      groupsFetching ||
      billingInfoFetching ||
      paymentMethodsFetching ||
      mdmFetching ||
      additionalInfoLoading ||
      mdmCertLoading;

    const superAdminCount =
      employees?.filter(e =>
        e.roles.some(r => r.slug === OrganizationUserRoleSlugs.SUPER_ADMIN)
      ).length ?? 0;

    const groupsWithApplications = orgGroupsResponse?.filter(
      g => g.applicationsCount > 0
    );

    const mdmEnabled =
      !!provisionStatusData &&
      mdmCertExpirationStatus !== MdmCertExpirationStatus.NO_CERTIFICATE;

    const integrationsConnected =
      !!orgAppsResponse?.applications?.some(
        app => app.integrationConnected && !app.isHrisSource
      ) || skipIntegrations;

    const addEmployeesComplete = (employees?.length ?? 0) >= 2;

    // AddEmployeesCard only renders with improvements on when the org has at least
    // one relevant entitlement. When it is hidden, all its substeps are N/A.
    const addEmployeesCardHidden =
      !!gettingStartedModuleImprovements &&
      !hasHrisIntegrationEntitlement &&
      !hasDirectHrisIntegrationEntitlement &&
      !hasEmployeeGroupsEntitlement &&
      !hasEmployeeUserAccountsEntitlement;

    const state: GettingStartedCompletionState = {
      connect_hr_system:
        addEmployeesCardHidden ||
        (gettingStartedModuleImprovements
          ? // Substep of AddEmployeesCard: done when HR connected, skipped, or
            // employees already added (card moves past the HR substep).
            !!hrisIntegrationId || skipHr || addEmployeesComplete
          : // Legacy standalone ConnectYourHRCard: only renders when the org has
            // an HRIS entitlement; done only when HR is actually connected or skipped.
            (!hasHrisIntegrationEntitlement &&
              !hasDirectHrisIntegrationEntitlement) ||
            !!hrisIntegrationId ||
            skipHr),
      add_employees: addEmployeesCardHidden || addEmployeesComplete,
      invite_admins: addEmployeesCardHidden || superAdminCount >= 2,
      add_applications: !hasApplicationsEntitlement
        ? true
        : !!orgAppsResponse?.applications?.some(app => !app.isHrisSource),
      connect_app_integration: !hasApplicationsEntitlement
        ? true
        : integrationsConnected,
      add_groups:
        !!(orgGroupsResponse?.length && groupsWithApplications?.length) ||
        skipGroups ||
        !hasEmployeeGroupsEntitlement,
      enable_mdm: mdmEnabled || skipMacMdm || !hasMdmEntitlement,
      add_payment_method:
        !!(hasBillingInfo && !!paymentMethods?.length) || skipPaymentMethod,
      configure_employee_support:
        addEmployeesCardHidden ||
        // Step is N/A when the org can't access Category Champions at all.
        !(hasXboardingsEntitlement || hasSupportCenterEntitlement) ||
        skipItChampions ||
        // Without improvements, the standalone card also requires the
        // employee accounts entitlement to render.
        (!gettingStartedModuleImprovements &&
          !hasEmployeeUserAccountsEntitlement),
      connect_abm:
        !hasMdmEntitlement ||
        skipMacMdm ||
        orgAdditionalInfoData?.abmStatus ===
          ABMEnrollmentStatusEnum.Connected ||
        skipAbm,
      configure_ztp: !hasMdmEntitlement || skipMacMdm || ztpConfigured,
      // notify_team is a substep of LaunchElectricCard (gated by goLive).
      // Mirror the card's notifyTeamCompleted={notifyTeam || completedLaunchElectricCard}
      // so the agent and quick prompts stay in sync with what the card displays.
      notify_team:
        !goLive ||
        !hasEmployeeUserAccountsEntitlement ||
        notifyTeam ||
        !!orgAdditionalInfoData?.sendAutomaticWelcomeEmails ||
        inviteEmployees,
      // LaunchElectricCard only renders when goLive is on and org has the entitlement.
      // Mark the step complete when the card isn't shown so it never appears as incomplete.
      invite_employees:
        !goLive || !hasEmployeeUserAccountsEntitlement
          ? true
          : !!orgAdditionalInfoData?.sendAutomaticWelcomeEmails ||
            inviteEmployees,
    };

    // When improvements are on, configure_employee_support is a substep of
    // AddEmployeesCard — promote it to group with the other AddEmployeesCard
    // substeps so it surfaces in quick prompts before card-level steps.
    const priorityOrder: GettingStartedStepKey[] =
      gettingStartedModuleImprovements
        ? [
            GETTING_STARTED_STEPS.CONNECT_HR_SYSTEM,
            GETTING_STARTED_STEPS.ADD_EMPLOYEES,
            GETTING_STARTED_STEPS.INVITE_ADMINS,
            GETTING_STARTED_STEPS.CONFIGURE_EMPLOYEE_SUPPORT,
            GETTING_STARTED_STEPS.ADD_APPLICATIONS,
            GETTING_STARTED_STEPS.CONNECT_APP_INTEGRATION,
            GETTING_STARTED_STEPS.ADD_GROUPS,
            GETTING_STARTED_STEPS.ADD_PAYMENT_METHOD,
            GETTING_STARTED_STEPS.ENABLE_MDM,
            GETTING_STARTED_STEPS.NOTIFY_TEAM,
            GETTING_STARTED_STEPS.INVITE_EMPLOYEES,
            GETTING_STARTED_STEPS.CONNECT_ABM,
            GETTING_STARTED_STEPS.CONFIGURE_ZTP,
          ]
        : GS_STEP_PRIORITY_ORDER;

    const incompleteStepKeys = priorityOrder.filter(key => {
      if (state[key]) return false;
      // Don't surface the HR prompt when employees already exist. In legacy mode
      // the ConnectYourHRCard may still show as incomplete, but the org reached
      // onboarding via a manual path — pushing HR as a top quick-prompt is misleading.
      if (
        key === GETTING_STARTED_STEPS.CONNECT_HR_SYSTEM &&
        addEmployeesComplete
      )
        return false;
      return true;
    });

    return {
      gettingStartedState: state,
      incompleteStepKeys,
      hasIncompleteSteps: incompleteStepKeys.length > 0,
      isLoading,
    };
  }, [
    skip,
    hrisIntegrationId,
    isLoadingProvider,
    skipHr,
    employees,
    employeesFetching,
    orgAppsResponse,
    appsFetching,
    orgGroupsResponse,
    groupsFetching,
    skipGroups,
    skipIntegrations,
    hasBillingInfo,
    billingInfoFetching,
    paymentMethods,
    paymentMethodsFetching,
    skipPaymentMethod,
    provisionStatusData,
    mdmFetching,
    mdmCertExpirationStatus,
    mdmCertLoading,
    skipMacMdm,
    orgAdditionalInfoData,
    additionalInfoLoading,
    skipAbm,
    skipItChampions,
    ztpConfigured,
    notifyTeam,
    inviteEmployees,
    hasApplicationsEntitlement,
    hasEmployeeGroupsEntitlement,
    hasMdmEntitlement,
    hasEmployeeUserAccountsEntitlement,
    hasHrisIntegrationEntitlement,
    hasDirectHrisIntegrationEntitlement,
    hasXboardingsEntitlement,
    hasSupportCenterEntitlement,
    gettingStartedModuleImprovements,
    goLive,
  ]);
};
